import { AZURE } from './api';

/**
 * Serviço para APIs de Mensagens
 * Base URL: /api/messaging
 */

// ========== CONVERSAS (INBOX) ==========

/**
 * Busca lista de conversas do usuário
 * @param {number} userId - ID do usuário
 * @param {Object} filters - Filtros opcionais (type, include_archived)
 * @returns {Promise<Array>} Lista de conversas
 */
export const getConversations = async (userId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('user_id', userId);
    if (filters.type) params.append('type', filters.type);
    if (filters.include_archived) params.append('include_archived', 'true');
    
    const response = await AZURE.get(`/messaging/conversations?${params.toString()}`);
    return response.data?.conversations || [];
  } catch (error) {
    console.error('Erro ao buscar conversas:', error);
    // Return empty array — real conversations will appear once the backend has data
    return [];
  }
};

/**
 * Cria ou retorna conversa direta (DM 1:1) existente
 * @param {number} userId1 - ID do primeiro usuário
 * @param {number} userId2 - ID do segundo usuário
 * @returns {Promise<Object>} Conversa criada ou existente
 */
export const createOrGetDirectConversation = async (userId1, userId2) => {
  try {
    const response = await AZURE.post('/messaging/conversations/direct', {
      user_id_1: userId1,
      user_id_2: userId2
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao criar/obter conversa:', error);
    throw error;
  }
};

/**
 * Busca detalhes de uma conversa específica
 * @param {number} conversationId - ID da conversa
 * @param {number} userId - ID do usuário solicitante
 * @returns {Promise<Object>} Detalhes da conversa com participantes
 */
export const getConversationDetails = async (conversationId, userId) => {
  try {
    const response = await AZURE.get(`/messaging/conversations/${conversationId}?user_id=${userId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar conversa:', error);
    throw error;
  }
};

// ========== MENSAGENS ==========

/**
 * Busca mensagens de uma conversa (paginadas)
 * @param {number} conversationId - ID da conversa
 * @param {number} userId - ID do usuário
 * @param {Object} options - Opções (limit, before_id para paginação)
 * @returns {Promise<Object>} Lista de mensagens + has_more
 */
export const getMessages = async (conversationId, userId, options = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('user_id', userId);
    if (options.limit) params.append('limit', options.limit);
    if (options.before_id) params.append('before_id', options.before_id);
    
    const response = await AZURE.get(`/messaging/conversations/${conversationId}/messages?${params.toString()}`);
    return {
      messages: response.data?.messages || [],
      has_more: response.data?.has_more || false
    };
  } catch (error) {
    console.error('Erro ao buscar mensagens:', error);
    // Fallback para mock data
    return getMockMessages(conversationId, userId);
  }
};

// Mock messages para desenvolvimento
const getMockMessages = (conversationId, userId) => {
  const messages = [
    {
      id: 1,
      conversation_id: conversationId,
      sender_user_id: 2,
      sender_name: 'César Cielo',
      sender_avatar: 'https://ui-avatars.com/api/?name=Cesar+Cielo&background=064e3b&color=fff',
      body: 'E aí, tudo bem?',
      type: 'text',
      created_at: new Date(Date.now() - 7200000).toISOString(),
      is_read: true
    },
    {
      id: 2,
      conversation_id: conversationId,
      sender_user_id: userId,
      sender_name: 'Você',
      sender_avatar: null,
      body: 'Tudo ótimo! E você?',
      type: 'text',
      created_at: new Date(Date.now() - 7000000).toISOString(),
      is_read: true
    },
    {
      id: 3,
      conversation_id: conversationId,
      sender_user_id: 2,
      sender_name: 'César Cielo',
      sender_avatar: 'https://ui-avatars.com/api/?name=Cesar+Cielo&background=064e3b&color=fff',
      body: 'Vamos treinar amanhã? Estou pensando em fazer um 800 livre.',
      type: 'text',
      created_at: new Date(Date.now() - 3600000).toISOString(),
      is_read: false
    }
  ];
  
  return {
    messages,
    has_more: false
  };
};

/**
 * Envia mensagem em uma conversa
 * @param {number} conversationId - ID da conversa
 * @param {number} userId - ID do remetente
 * @param {string} body - Texto da mensagem
 * @param {string} type - Tipo da mensagem (text, media, card)
 * @returns {Promise<Object>} Mensagem enviada
 */
export const sendMessage = async (conversationId, userId, body, type = 'text') => {
  try {
    const response = await AZURE.post(`/messaging/conversations/${conversationId}/messages`, {
      user_id: userId,
      body,
      type
    });
    return response.data?.message || null;
  } catch (error) {
    console.error('Erro ao enviar mensagem:', error);
    // Fallback mock - simula envio bem-sucedido
    return {
      id: Date.now(),
      conversation_id: conversationId,
      sender_user_id: userId,
      sender_name: 'Você',
      sender_avatar: null,
      body,
      type,
      created_at: new Date().toISOString(),
      is_read: false
    };
  }
};

/**
 * Marca mensagens como lidas até um determinado ID
 * @param {number} conversationId - ID da conversa
 * @param {number} userId - ID do usuário
 * @param {number} messageId - ID da última mensagem lida
 * @returns {Promise<Object>} Confirmação
 */
export const markAsRead = async (conversationId, userId, messageId) => {
  try {
    const response = await AZURE.post(`/messaging/conversations/${conversationId}/read`, {
      user_id: userId,
      message_id: messageId
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao marcar como lida:', error);
    // Fallback mock - simula sucesso
    return { success: true, message: 'Mock: Mensagem marcada como lida' };
  }
};

// ========== BLOQUEIOS ==========

/**
 * Bloqueia um usuário globalmente
 * @param {number} blockerUserId - ID de quem está bloqueando
 * @param {number} blockedUserId - ID de quem será bloqueado
 * @param {string} reason - Motivo do bloqueio (opcional)
 * @returns {Promise<Object>} Confirmação
 */
export const blockUser = async (blockerUserId, blockedUserId, reason = null) => {
  try {
    const response = await AZURE.post('/messaging/blocks', {
      blocker_user_id: blockerUserId,
      blocked_user_id: blockedUserId,
      reason
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao bloquear usuário:', error);
    throw error;
  }
};

/**
 * Desbloqueia um usuário
 * @param {number} blockerUserId - ID de quem bloqueou
 * @param {number} blockedUserId - ID de quem será desbloqueado
 * @returns {Promise<Object>} Confirmação
 */
export const unblockUser = async (blockerUserId, blockedUserId) => {
  try {
    const response = await AZURE.delete(`/messaging/blocks/${blockedUserId}?user_id=${blockerUserId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao desbloquear usuário:', error);
    throw error;
  }
};

// ========== HEALTH CHECK ==========

/**
 * Verifica saúde da API de mensagens
 * @returns {Promise<Object>} Status da API
 */
export const healthCheck = async () => {
  try {
    const response = await AZURE.get('/messaging/health');
    return response.data;
  } catch (error) {
    console.error('Erro no health check:', error);
    throw error;
  }
};

// Exporta todas as funções como objeto default também
export default {
  getConversations,
  createOrGetDirectConversation,
  getConversationDetails,
  getMessages,
  sendMessage,
  markAsRead,
  blockUser,
  unblockUser,
  healthCheck
};
